import React, { useState } from "react";
import { CoachSocialNetworkProfile, SocialAccountOption, PlayerSearchResult } from "@/services/bedrock";
import { SocialNetworkIcon } from "@/components/SocialNetworkIcon";
import { socialNetworkConnectionNames, socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { Button, Dialog, Table } from "@/components/ui";
import { Plus, CheckIcon, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { StagedAssignment } from "./BulkAssignDialog";

export interface ImportMappingsDialogProps {
  profile: CoachSocialNetworkProfile;
  players: PlayerSearchResult[];
  accountOptions: SocialAccountOption[];
  onClose: () => void;
  onImportStaged: (staged: StagedAssignment[]) => void;
}

export function ImportMappingsDialog({
  profile,
  players,
  accountOptions,
  onClose,
  onImportStaged,
}: ImportMappingsDialogProps) {
  const [importStep, setImportStep] = useState<"upload" | "review">("upload");
  const [csvMatches, setCsvMatches] = useState<StagedAssignment[]>([]);
  const [unresolvedRows, setUnresolvedRows] = useState<
    { rowNum: number; player_id: string; connection: string; reason: string }[]
  >([]);

  const platformName = socialNetworkNames[profile.socialNetworkId];
  const accountLabel = socialNetworkConnectionNames[profile.socialNetworkId];

  const getExampleId = (networkId: SocialNetworksId) => {
    switch (networkId) {
      case "FB":
        return "123456789012345";
      case "IG":
        return "12345678901234567";
      case "GL":
        return "locations/1234567890123456789";
      case "LI":
        return "li_abc123XYZ";
      default:
        return "ID-123456";
    }
  };

  const getExampleName = (networkId: SocialNetworksId) => {
    switch (networkId) {
      case "FB":
        return "Sample Facebook Page";
      case "IG":
        return "Sample Instagram Account";
      case "GL":
        return "Sample Google Location";
      case "LI":
        return "Sample LinkedIn Company";
      default:
        return "Sample Social Account";
    }
  };

  // Safe helper getters locally
  const getPlayerFirstName = (p: PlayerSearchResult) => p.firstName || (p as any).playerAccount?.firstName || "";
  const getPlayerLastName = (p: PlayerSearchResult) => p.lastName || (p as any).playerAccount?.lastName || "";
  const getPlayerEmail = (p: PlayerSearchResult) => p.email || (p as any).playerAccount?.email || "";

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      if (!text) return;

      try {
        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
        if (lines.length < 2) {
          toast.error("CSV file is empty or missing data rows.");
          return;
        }

        // Parse headers
        const headers = lines[0].split(",").map(h => h.trim().replace(/^["']|["']$/g, ""));
        const playerIdIndex = headers.findIndex(h => h.toLowerCase() === "player_id");
        const connectionIndex = headers.findIndex(h => h.toLowerCase() === "connection");

        if (playerIdIndex === -1 || connectionIndex === -1) {
          toast.error("CSV header must contain exactly 'player_id' and 'connection' columns.");
          return;
        }

        const matches: StagedAssignment[] = [];
        const skipped: typeof unresolvedRows = [];

        for (let i = 1; i < lines.length; i++) {
          const row = lines[i].split(",").map(cell => cell.trim().replace(/^["']|["']$/g, ""));
          if (row.length <= Math.max(playerIdIndex, connectionIndex)) continue;

          const player_id = row[playerIdIndex];
          const connection = row[connectionIndex];

          if (!player_id || !connection) continue;

          // Match player
          const matchedPlayer = players.find(
            p =>
              p.guid === player_id ||
              p.internalIdentifier === player_id ||
              p.email?.toLowerCase() === player_id.toLowerCase()
          );

          // Match connection option
          const matchedOption = accountOptions.find(o => o.id === connection || o.name === connection);

          if (!matchedPlayer && !matchedOption) {
            skipped.push({
              rowNum: i + 1,
              player_id,
              connection,
              reason: "Player and Connection could not be resolved.",
            });
          } else if (!matchedPlayer) {
            skipped.push({ rowNum: i + 1, player_id, connection, reason: `Player '${player_id}' not found.` });
          } else if (!matchedOption) {
            skipped.push({
              rowNum: i + 1,
              player_id,
              connection,
              reason: `Connection option '${connection}' not found.`,
            });
          } else {
            matches.push({
              player: matchedPlayer,
              connection: matchedOption,
            });
          }
        }

        setCsvMatches(matches);
        setUnresolvedRows(skipped);
        setImportStep("review");

        toast.info(`CSV Parse Complete: matched ${matches.length} pairs, skipped ${skipped.length} rows.`);
      } catch (err) {
        console.error("CSV parse error:", err);
        toast.error("An error occurred while parsing the CSV file.");
      }
    };

    reader.readAsText(file);
  };

  const handleRemoveMatch = (connectionId: string) => {
    setCsvMatches(prev => prev.filter(s => s.connection.id !== connectionId));
  };

  const handleStageConnections = () => {
    if (csvMatches.length === 0) {
      toast.warning("No matched connections to stage.");
      return;
    }
    onImportStaged(csvMatches);
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()} size="xl" showCloseButton>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2">
          <SocialNetworkIcon socialNetworkId={profile.socialNetworkId} size="mini" />
          Import CSV Mappings — {platformName}
        </Dialog.Title>
        <Dialog.Description>
          {importStep === "upload"
            ? "Upload a comma-separated CSV file to automatically map connections to players."
            : "Audit matched listings before adding them to the staged builder queue."}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="min-h-[380px] max-h-[500px] overflow-y-auto flex flex-col gap-4">
        {importStep === "upload" ? (
          <div className="flex flex-col gap-4 w-full">
            {/* Instructions box */}
            <div className="text-xs text-muted-foreground bg-gray-50 border border-gray-200/50 p-4 rounded-lg flex flex-col gap-3">
              <span className="font-bold text-foreground text-sm">CSV File Requirements & Column Mapping:</span>
              <ul className="list-disc pl-5 flex flex-col gap-1.5 m-0">
                <li>
                  Your file must contain a header row with columns:{" "}
                  <code className="font-bold text-primary">player_id</code> and{" "}
                  <code className="font-bold text-primary">connection</code>.
                </li>
                <li>
                  <span className="font-semibold text-foreground">player_id matches</span>: exact match on{" "}
                  <span className="font-semibold">Player API GUID</span> or{" "}
                  <span className="font-semibold">Internal Identifier</span>, or case-insensitive match on{" "}
                  <span className="font-semibold">Player Email</span>.
                </li>
                <li>
                  <span className="font-semibold text-foreground">connection matches</span>: exact match of the
                  sub-account/listing <span className="font-semibold">ID</span> or exact listing{" "}
                  <span className="font-semibold">Name</span>.
                </li>
              </ul>

              <div className="border-t border-gray-200/50 pt-3 mt-1 flex flex-col gap-2">
                <span className="font-bold text-foreground text-xs">Sample CSV Format:</span>
                <div className="border border-gray-100 rounded-md overflow-hidden bg-background">
                  <Table className="text-[10px] leading-none">
                    <Table.Header className="bg-gray-50/50">
                      <Table.Row>
                        <Table.Head className="font-bold py-1 h-6">player_id</Table.Head>
                        <Table.Head className="font-bold py-1 h-6">connection</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row className="h-6 hover:bg-transparent border-b border-gray-100">
                        <Table.Cell className="py-1 font-semibold">jane.doe@agency.com</Table.Cell>
                        <Table.Cell className="py-1 text-primary font-mono">
                          {getExampleId(profile.socialNetworkId)}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row className="h-6 hover:bg-transparent">
                        <Table.Cell className="py-1 font-semibold">player-internal-id-5</Table.Cell>
                        <Table.Cell className="py-1 text-primary">{getExampleName(profile.socialNetworkId)}</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </div>
              </div>
            </div>

            {/* Custom file selector drag-drop look */}
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-10 flex flex-col items-center justify-center gap-3 text-center bg-gray-50/20 hover:bg-gray-50/50 transition-colors cursor-pointer relative">
              <Plus className="size-8 text-primary opacity-60" />
              <div className="flex flex-col gap-1 text-center">
                <span className="text-sm font-bold text-foreground">Upload CSV File</span>
                <span className="text-xs text-muted-foreground">
                  Click to browse your computer or drag and drop here
                </span>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </div>
          </div>
        ) : (
          /* Step 2: Audit Matched Pairs */
          <div className="flex flex-col gap-4">
            {/* Audit metrics alert */}
            <div className="bg-primary/5 p-3 rounded-lg border border-primary/10 flex flex-col gap-1">
              <span className="text-xs font-bold text-primary flex items-center gap-1.5">
                <CheckIcon className="size-3.5" />
                CSV Parse Summary:
              </span>
              <span className="text-xs text-muted-foreground">
                Successfully matched <span className="font-bold text-foreground">{csvMatches.length} listings</span>.
                Skipped {unresolvedRows.length} unresolvable rows.
              </span>
            </div>

            {/* Matched mappings list */}
            {csvMatches.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-10 text-center m-0">
                No successful matches found to stage.
              </p>
            ) : (
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <Table className="text-xs">
                  <Table.Header className="bg-gray-50">
                    <Table.Row>
                      <Table.Head className="font-bold">Player Name</Table.Head>
                      <Table.Head className="font-bold">Player Email</Table.Head>
                      <Table.Head className="font-bold">
                        Assigned {platformName} {accountLabel}
                      </Table.Head>
                      <Table.Head className="font-bold text-right">Action</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {csvMatches.map(st => (
                      <Table.Row key={`csv-staged-${st.connection.id}`} className="hover:bg-gray-50/50">
                        <Table.Cell className="font-semibold">
                          {getPlayerFirstName(st.player)} {getPlayerLastName(st.player)}
                        </Table.Cell>
                        <Table.Cell className="text-muted-foreground">{getPlayerEmail(st.player)}</Table.Cell>
                        <Table.Cell className="font-semibold text-primary">{st.connection.name}</Table.Cell>
                        <Table.Cell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="text-destructive hover:bg-destructive/10 h-6 w-6"
                            onClick={() => handleRemoveMatch(st.connection.id)}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}

            {/* Warnings drawer/expandable for Skipped rows */}
            {unresolvedRows.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 p-3 rounded-lg flex flex-col gap-2">
                <span className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                  <AlertCircle className="size-3.5 text-amber-600" />
                  Audit Warnings ({unresolvedRows.length} rows skipped):
                </span>
                <div className="max-h-[100px] overflow-y-auto flex flex-col gap-1 pr-1">
                  {unresolvedRows.map((skip, idx) => (
                    <div key={`skip-${idx}`} className="text-[10px] text-amber-700 font-medium leading-normal">
                      • Line {skip.rowNum}: player_id: &quot;{skip.player_id}&quot;, connection: &quot;{skip.connection}
                      &quot; — <span className="font-bold">{skip.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer className="shrink-0 gap-2">
        {importStep === "upload" ? (
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => setImportStep("upload")}>
              ← Back
            </Button>
            <Button
              variant="default"
              onClick={handleStageConnections}
              disabled={csvMatches.length === 0}
              className="font-bold flex items-center gap-1.5"
            >
              Stage Connections ({csvMatches.length})
            </Button>
          </>
        )}
      </Dialog.Footer>
    </Dialog>
  );
}
